import { z } from "zod";
import { ulid } from "ulidx";
import { SuggestedActionQuality } from "@acepoker/api-core";
import { ActionSchema } from "./action";
import type { PlayerPosition } from "./acepoker";
import type { PokerConfig } from "@acepoker/poker-core/src/facade/poker";

export enum PreflopAction {
  Srp = "single_raised_pot",
  LimpedPot = "limped_pot",
}
export const PreflopActionValues = [
  PreflopAction.Srp,
  PreflopAction.LimpedPot,
] as const;

export enum FlopAction {
  Checked = "checked",
  BetCall = "bet_call",
}
export const FlopActionValues = [
  FlopAction.Checked,
  FlopAction.BetCall,
] as const;

export enum TurnAction {
  BetCall = "bet_call",
  Checked = "checked",
}
export const TurnActionValues = [
  TurnAction.BetCall,
  TurnAction.Checked,
] as const;
export const TurnActionSchema = z.enum(TurnActionValues);

export enum Street {
  Preflop = "preflop",
  Flop = "flop",
  Turn = "turn",
  River = "river",
}
export const StreetValues = [
  Street.Preflop,
  Street.Flop,
  Street.Turn,
  Street.River,
] as const;

export enum StreetFilter {
  Preflop = "preflop",
  Flop = "flop",
  Turn = "turn",
  River = "river",
  None = "none",
}
export const StreetFilterValues = [
  StreetFilter.Preflop,
  StreetFilter.Flop,
  StreetFilter.Turn,
  StreetFilter.River,
  StreetFilter.None,
] as const;

export enum Position {
  BTN = "BTN",
  SB = "SB",
  BB = "BB",
  STR = "STR",
  UTG = "UTG",
  UTG1 = "UTG1",
  HJ = "HJ",
  CO = "CO",
}
export const PositionValues = [
  Position.BTN,
  Position.SB,
  Position.BB,
  Position.STR,
  Position.UTG,
  Position.UTG1,
  Position.HJ,
  Position.CO,
] as const;

export enum PositionGroup {
  Early = "early",
  Middle = "middle",
  Late = "late",
  Blinds = "blinds",
  BlindsStr = "blindsStr",
  None = "none",
}
export const PositionGroupValues = [
  PositionGroup.Early,
  PositionGroup.Middle,
  PositionGroup.Late,
  PositionGroup.Blinds,
  PositionGroup.BlindsStr,
  PositionGroup.None,
] as const;
export const PositionGroupValuesDefault = [
  PositionGroup.Early,
  PositionGroup.Middle,
  PositionGroup.Late,
  PositionGroup.Blinds,
] as const;

export enum PositionGroupFilter {
  Early = "early",
  Middle = "middle",
  Late = "late",
  Blinds = "blinds",
  None = "none",
}
export const PositionGroupFilterValues = [
  PositionGroupFilter.Early,
  PositionGroupFilter.Middle,
  PositionGroupFilter.Late,
  PositionGroupFilter.Blinds,
  PositionGroupFilter.None,
] as const;

export enum PreflopScenario {
  FirstToAct = "first_to_act",
  VsLimps = "vs_limps",
  VsRaises = "vs_raises",
  Vs3Bet = "vs_3bet",
  VsIso = "vs_iso",
}
export const PreflopScenarioValues = [
  PreflopScenario.FirstToAct,
  PreflopScenario.VsLimps,
  PreflopScenario.VsRaises,
  PreflopScenario.Vs3Bet,
  PreflopScenario.VsIso,
] as const;

export enum FlopScenario {
  FirstToAct = "first_to_act",
  VsCheck = "vs_check",
  VsBet = "vs_bet",
  VsRaises = "vs_raises",
}
export const FlopScenarioValues = [
  FlopScenario.FirstToAct,
  FlopScenario.VsCheck,
  FlopScenario.VsBet,
  FlopScenario.VsRaises,
] as const;

export enum TurnScenario {
  FirstToAct = "first_to_act",
  VsCheck = "vs_check",
  VsBet = "vs_bet",
}
export const TurnScenarioValues = [
  TurnScenario.FirstToAct,
  TurnScenario.VsCheck,
  TurnScenario.VsBet,
] as const;

export enum RiverScenario {
  FirstToAct = "first_to_act",
  VsBet = "vs_bet",
  VsCheck = "vs_check",
}
export const RiverScenarioValues = [
  RiverScenario.FirstToAct,
  RiverScenario.VsBet,
  RiverScenario.VsCheck,
] as const;

export const PreflopFirstToActSchema = z.object({
  street: z.literal(Street.Preflop),
  scenario_type: z.literal(PreflopScenario.FirstToAct),
  hero_position: z.enum(PositionGroupValues),
});
export type PreflopFirstToAct = z.infer<typeof PreflopFirstToActSchema>;

export const PreflopVsLimpsSchema = z.object({
  street: z.literal(Street.Preflop),
  scenario_type: z.literal(PreflopScenario.VsLimps),
  hero_position: z.enum(PositionGroupValues),
  limper_positions: z.array(z.enum(PositionGroupValues)),
});
export type PreflopVsLimps = z.infer<typeof PreflopVsLimpsSchema>;

export const PreflopVsRaisesSchema = z.object({
  street: z.literal(Street.Preflop),
  scenario_type: z.literal(PreflopScenario.VsRaises),
  hero_position: z.enum(PositionGroupValues),
  raisers_positions: z.array(z.enum(PositionGroupValues)),
  additional_callers: z.array(z.enum(PositionGroupValues)),
});
export type PreflopVsRaises = z.infer<typeof PreflopVsRaisesSchema>;

export const PreflopVs3BetSchema = z.object({
  street: z.literal(Street.Preflop),
  scenario_type: z.literal(PreflopScenario.Vs3Bet),
  hero_position: z.enum(PositionGroupValues),
  three_bet_position: z.enum(PositionGroupValues),
  additional_callers: z.array(z.enum(PositionGroupValues)),
});
export type PreflopVs3Bet = z.infer<typeof PreflopVs3BetSchema>;

export const PreflopVsIsoSchema = z.object({
  street: z.literal(Street.Preflop),
  scenario_type: z.literal(PreflopScenario.VsIso),
  hero_position: z.enum(PositionGroupValues),
  iso_position: z.enum(PositionGroupValues),
  additional_limpers: z.array(z.enum(PositionGroupValues)),
});
export type PreflopVsIso = z.infer<typeof PreflopVsIsoSchema>;

export const FlopFirstToActSchema = z.object({
  street: z.literal(Street.Flop),
  scenario_type: z.literal(FlopScenario.FirstToAct),
  preflop_action: z.enum(PreflopActionValues),
  hero_position: z.enum(PositionGroupValues),
  opponent_positions: z.array(z.enum(PositionGroupValues)),
});
export type FlopFirstToAct = z.infer<typeof FlopFirstToActSchema>;

export const FlopVsCheckSchema = z.object({
  street: z.literal(Street.Flop),
  scenario_type: z.literal(FlopScenario.VsCheck),
  preflop_action: z.enum(PreflopActionValues),
  hero_position: z.enum(PositionGroupValues),
  opponent_positions: z.array(z.enum(PositionGroupValues)),
});
export type FlopVsCheck = z.infer<typeof FlopVsCheckSchema>;

export const FlopVsBetSchema = z.object({
  street: z.literal(Street.Flop),
  scenario_type: z.literal(FlopScenario.VsBet),
  preflop_action: z.enum(PreflopActionValues),
  hero_position: z.enum(PositionGroupValues),
  bet_position: z.enum(PositionGroupValues),
  additional_callers: z.array(z.enum(PositionGroupValues)),
});
export type FlopVsBet = z.infer<typeof FlopVsBetSchema>;

export const FlopVsRaisesSchema = z.object({
  street: z.literal(Street.Flop),
  scenario_type: z.literal(FlopScenario.VsRaises),
  preflop_action: z.enum(PreflopActionValues),
  hero_position: z.enum(PositionGroupValues),
  raisers_positions: z.array(z.enum(PositionGroupValues)),
  additional_callers: z.array(z.enum(PositionGroupValues)),
});
export type FlopVsRaises = z.infer<typeof FlopVsRaisesSchema>;

export const TurnFirstToActSchema = z.object({
  street: z.literal(Street.Turn),
  scenario_type: z.literal(TurnScenario.FirstToAct),
  preflop_action: z.enum(PreflopActionValues),
  flop_action: z.enum(FlopActionValues),
  hero_position: z.enum(PositionGroupValues),
  opponent_positions: z.union([
    z.array(z.enum(PositionGroupValues)),
    z.enum(PositionGroupValues),
  ]),
});
export type TurnFirstToAct = z.infer<typeof TurnFirstToActSchema>;

export const TurnVsCheckSchema = z.object({
  street: z.literal(Street.Turn),
  scenario_type: z.literal(TurnScenario.VsCheck),
  preflop_action: z.enum(PreflopActionValues),
  flop_action: z.enum(FlopActionValues),
  hero_position: z.enum(PositionGroupValues),
  opponent_positions: z.union([
    z.array(z.enum(PositionGroupValues)),
    z.enum(PositionGroupValues),
  ]),
});
export type TurnVsCheck = z.infer<typeof TurnVsCheckSchema>;

export const TurnVsBetSchema = z.object({
  street: z.literal(Street.Turn),
  scenario_type: z.literal(TurnScenario.VsBet),
  preflop_action: z.enum(PreflopActionValues),
  flop_action: z.enum(FlopActionValues),
  hero_position: z.enum(PositionGroupValues),
  bet_position: z.enum(PositionGroupValues),
});
export type TurnVsBet = z.infer<typeof TurnVsBetSchema>;

export const RiverFirstToActSchema = z.object({
  street: z.literal(Street.River),
  scenario_type: z.literal(RiverScenario.FirstToAct),
  preflop_action: z.enum(PreflopActionValues),
  flop_action: z.literal(FlopAction.BetCall),
  turn_action: z.enum(TurnActionValues),
  hero_position: z.enum(PositionGroupValues),
  opponent_positions: z.union([
    z.array(z.enum(PositionGroupValues)),
    z.enum(PositionGroupValues),
  ]),
});
export type RiverFirstToAct = z.infer<typeof RiverFirstToActSchema>;

export const RiverVsCheckSchema = z.object({
  street: z.literal(Street.River),
  scenario_type: z.literal(RiverScenario.VsCheck),
  preflop_action: z.enum(PreflopActionValues),
  flop_action: z.literal(FlopAction.BetCall),
  turn_action: z.enum(TurnActionValues),
  hero_position: z.enum(PositionGroupValues),
  opponent_positions: z.union([
    z.array(z.enum(PositionGroupValues)),
    z.enum(PositionGroupValues),
  ]),
});
export type RiverVsCheck = z.infer<typeof RiverVsCheckSchema>;

export const RiverVsBetSchema = z.object({
  street: z.literal(Street.River),
  scenario_type: z.literal(RiverScenario.VsBet),
  preflop_action: z.enum(PreflopActionValues),
  flop_action: z.literal(FlopAction.BetCall),
  turn_action: z.enum(TurnActionValues),
  hero_position: z.enum(PositionGroupValues),
  bet_position: z.enum(PositionGroupValues),
});
export type RiverVsBet = z.infer<typeof RiverVsBetSchema>;

export const PredefinedSchema = z.object({
  id: z.number(),
  street: z.enum(StreetValues),
});

export const CustomDrillSchema = z.null();

export const DrillConfigSchema = z.union([
  PreflopFirstToActSchema,
  PreflopVsLimpsSchema,
  PreflopVsRaisesSchema,
  PreflopVs3BetSchema,
  PreflopVsIsoSchema,

  FlopFirstToActSchema,
  FlopVsCheckSchema,
  FlopVsBetSchema,
  FlopVsRaisesSchema,

  TurnFirstToActSchema,
  TurnVsCheckSchema,
  TurnVsBetSchema,

  RiverFirstToActSchema,
  RiverVsCheckSchema,
  RiverVsBetSchema,

  PredefinedSchema,

  CustomDrillSchema,
]);
export type DrillConfig = z.infer<typeof DrillConfigSchema>;

export type PredefinedDrillConfig = Record<
  string,
  string | string[] | { oneOf: string[] }
>;

export type PredefinedDrillTemplate = {
  id: number;
  name: string;
  street: StreetFilter;
  hero_position: PositionGroupFilter[];
  config: PredefinedDrillConfig | PredefinedDrillConfig[];
};

export const GamingConfigurationSchema = z.object({
  game: z.string().optional().default("NLHE"),
  blinds: z
    .array(z.number().gt(0))
    .refine(([sb, bb]) => sb! < bb!, {
      message: "Small blind must be less than big blind",
      path: [0],
    })
    .refine(
      ([, bb, straddle]) => {
        return straddle ? bb! < straddle : true;
      },
      {
        message: `Straddle must be bigger than big blind`,
        path: [1],
      },
    )
    .default([50, 100, 200]),
  antes: z.number().optional().default(0),
});

export type GamingConfiguration = z.infer<typeof GamingConfigurationSchema>;

export const RebuyConfigurationSchema = z
  .object({
    rebuy_trigger: z.number().gte(0).default(0),
    rebuy_amount: z.number().gt(0).default(10000),
    buyout_trigger: z.number().gt(0).default(100000),
    buyout_amount: z.number().gt(0).default(100),
  })
  .refine((rebuy) => rebuy.rebuy_trigger < rebuy.rebuy_amount, {
    message: "Amount must be more than rebuy trigger",
    path: ["rebuy_amount"],
  })
  .refine((rebuy) => rebuy.buyout_trigger > rebuy.buyout_amount, {
    message: "Amount must be less than buyout trigger",
    path: ["buyout_amount"],
  })
  .refine((rebuy) => rebuy.rebuy_trigger < rebuy.buyout_trigger, {
    message: "Rebuy trigger must be less than buyout trigger",
    path: ["buyout_trigger"],
  })
  .refine((rebuy) => rebuy.buyout_trigger > rebuy.rebuy_amount, {
    message: "Buyout trigger must be less than rebuy amount",
    path: ["buyout_trigger"],
  })
  .default({
    rebuy_trigger: 0,
    rebuy_amount: 10000,
    buyout_trigger: 1000000,
    buyout_amount: 10000,
  });

const UserIdsSchema = z
  .array(
    z.object({
      name: z.string(),
      user_id: z.string(),
      bot: z.boolean(),
      seat: z.string().optional(),
      avatar: z.string().optional(),
      nickname: z.string().optional(),
      bot_id: z.string().optional(),
    }),
  )
  .default([]);

const JoinRequestsSchema = z
  .array(
    z.object({
      user_id: z.string(),
      approved: z.boolean(),
      declined: z.boolean(),
      nickname: z.string().optional(),
      avatar: z.string().optional(),
      chips: z.number().optional(),
      created_at: z.string().datetime({ offset: true }),
      updated_at: z.string().datetime({ offset: true }),
    }),
  )
  .default([]);

export const AcePokerTableSchema = z.object({
  table_id: z.string().default(() => ulid()),
  gaming_configuration: GamingConfigurationSchema,

  max_players: z.number().min(2).max(9).default(9),

  currency: z.string().optional().default("USD"),

  learning_mode: z.boolean().default(false),
  assessment_mode: z.boolean().default(false),
  preconfigured_mode: z.boolean().default(false),

  starting_stack: z.number().gt(0).default(10000),
  rebuy_configuration: RebuyConfigurationSchema,

  bots_count: z.number().default(0),

  host_id: z.string().optional().nullable(),

  createdAt: z.string().datetime({ offset: true }),
  lastUpdatedAt: z.string().datetime({ offset: true }),
  lastPlayerActionAt: z.string().datetime({ offset: true }),
});

export type AcePokerTable = z.infer<typeof AcePokerTableSchema>;
export type AcePokerTableUserId = z.infer<typeof UserIdsSchema>[0];
export type AcePokerTableJoinRequest = z.infer<typeof JoinRequestsSchema>[0];

export const AcePokerTableCreateSchema = AcePokerTableSchema;

export type AcePokerTableCreate = Omit<
  z.infer<typeof AcePokerTableCreateSchema>,
  "join_requests"
>;

export const AcePokerTableUpdateSchema = AcePokerTableSchema.omit({
  table_id: true,
}).partial();

export type AcePokerTableUpdate = z.infer<typeof AcePokerTableUpdateSchema>;

export const SuggestedActionSchema = z.object({
  actionName: z.string(),
  actionNameRaw: z.string(),
  ev: z.number(),
  strategy: z.number(),
  amount: z.number().nullable().optional(),
  score: z.number(),
  quality: z.enum([
    SuggestedActionQuality.Best,
    SuggestedActionQuality.Blunder,
    SuggestedActionQuality.Correct,
    SuggestedActionQuality.Inaccuracy,
    SuggestedActionQuality.Wrong,
  ]),
});

export type SuggestedAction = z.infer<typeof SuggestedActionSchema>;

export const PlayerLogTablePlayerSchema = z.object({
  uid: z.string(),
  name: z.string(),
  stack: z.number(),
  seat_no: z.number(),
  position: z.string(),
  hole_cards: z.string().nullable().optional(),
});

export type PlayerLogTablePlayer = z.infer<typeof PlayerLogTablePlayerSchema>;

export const PlayerLogHandStateSchema = z.object({
  game_type: z.string(),
  game_type_code: z.string(),
  game_mode_code: z.string(),
  big_blind: z.number(),
  ante: z.number(),
  dealer_seat: z.number(),
  sb_seat: z.number(),
  bb_seat: z.number(),
  straddle_seat: z.number(),
  started_at: z.string(),
  flop_pot: z.number().nullable().optional(),
  turn_pot: z.number().nullable().optional(),
  river_pot: z.number().nullable().optional(),
  final_pot: z.number().nullable().optional(),
  pot_size: z.number().nullable().optional(),
  post_seats: z.array(z.string()),
  win_bet: z.number().nullable().optional(),
});

export type PlayerLogHandState = z.infer<typeof PlayerLogHandStateSchema>;

export const PlayerLogSchema = z.object({
  id: z.number(),
  playerId: z.string().nullable().optional(),
  drillId: z.number().nullable().optional(),
  tableId: z.string().nullable().optional(),
  handId: z.string(),
  handUuid: z.string(),
  moveId: z.string().max(36).min(36),
  actionName: z.string(),
  actionNameRaw: z.string(),
  ev: z.number(),
  strategy: z.number(),
  amount: z.number().nullable().optional(),
  score: z.number(),
  quality: z
    .enum([
      SuggestedActionQuality.Best,
      SuggestedActionQuality.Blunder,
      SuggestedActionQuality.Correct,
      SuggestedActionQuality.Inaccuracy,
      SuggestedActionQuality.Wrong,
    ])
    .nullable(),
  sessionId: z.string().max(36).min(36),
  playerCards: z.string().default(""),
  communityCards: z.string().default(""),
  suggestedActions: z.array(SuggestedActionSchema).default([]),
  handHistory: z.array(ActionSchema).default([]),
  players: z.array(PlayerLogTablePlayerSchema).default([]),
  handState: PlayerLogHandStateSchema,

  createdAt: z.string().datetime({ offset: true }),
});

export type PlayerLog = z.infer<typeof PlayerLogSchema>;

export const PlayerLogCreateSchema = PlayerLogSchema.omit({ id: true }).partial(
  {
    suggestedActions: true,
    createdAt: true,
  },
);

export type PlayerLogCreate = z.infer<typeof PlayerLogCreateSchema>;

export enum StopAt {
  Move = "move",
  Street = "street",
  Hand = "hand",
}

export const StopAtValues = [StopAt.Move, StopAt.Hand, StopAt.Street] as const;

export enum GameFlavor {
  BombPot = "bombPot",
}

export const GameFlavorValues = [GameFlavor.BombPot] as const;

export const DrillBaseSchema = z.object({
  id: z.number(),
  name: z.string().optional().nullable(),
  description: z.string().optional().nullable(),
  gaming_configuration: GamingConfigurationSchema,

  max_players: z.number().min(2).max(9).default(9),

  currency: z.string().optional().default("USD"),

  learning_mode: z.boolean().default(false),

  starting_stack: z.number().gt(0).default(10000),
  rebuy_configuration: RebuyConfigurationSchema,

  stopAt: z.enum(StopAtValues),

  creatorId: z.string().optional().nullable(),
  createdAt: z.string().datetime({ offset: true }),
  handsToPlay: z.number().default(100).nullable(),
});

export const A5DrillDataSchema = z.object({
  positionGroup: z.string().nullable().optional(),
  positionAuthority: z.string().nullable().optional(),
  raiseCount: z.string().nullable().optional(),
  opponentsCount: z.string().nullable().optional(),
  anteMultiplier: z.string().nullable().optional(),
  preflopRaiseCount: z.string().nullable().optional(),
  postflopRaiseCount: z.string().nullable().optional(),
});

export type A5DrillData = z.infer<typeof A5DrillDataSchema>;

export enum DrillKind {
  Sequence = "sequence",
  Street = "street",
}

export const DrillKindValues = [DrillKind.Sequence, DrillKind.Street] as const;

export const DrillDataSchema = z.object({
  kind: z.enum(DrillKindValues),
  flavors: z.array(z.string()),
  data: DrillConfigSchema,
});

export type DrillKindData = z.infer<typeof DrillDataSchema>;

export const DrillSchema = z.intersection(
  DrillBaseSchema,
  DrillDataSchema,
  A5DrillDataSchema,
);

export type Drill = z.infer<typeof DrillSchema>;

export const DrillCreateSchema = z.intersection(
  DrillBaseSchema.omit({ id: true }).partial({
    max_players: true,
    currency: true,
    learning_mode: true,
    starting_stack: true,
    stopAt: true,
    createdAt: true,
  }),
  DrillDataSchema,
  A5DrillDataSchema.partial(),
);

export type DrillCreate = z.infer<typeof DrillCreateSchema>;

export const DrillUpdateSchema = z.intersection(
  DrillBaseSchema.omit({
    id: true,
    createdAt: true,
  }).partial(),
  DrillDataSchema,
  A5DrillDataSchema.partial(),
);
export type DrillUpdate = z.infer<typeof DrillUpdateSchema>;

export const PlayerSchema = z.object({
  id: z.number(),
  player_id: z.string(),
  enabled: z.boolean().default(true),
  email: z.string().optional(),
  experience: z.number().default(0),
  status: z
    .enum(["available", "created", "playing", "playing_error"])
    .optional()
    .default("available"),
});

export type Player = z.infer<typeof PlayerSchema>;

export const PlayerStatsSchema = z.object({
  duration_days: z.number(),
  win_bb: z.number(),
  total_hands: z.number(),
  sessions: z.number(),
  label: z.string(),
  blind_level: z.string(),
  "pre-flop": z.object({
    VPIP: z.number(),
    PFR: z.number(),
    ATS: z.number(),
    FTS: z.number(),
    "3-bet": z.number(),
    F3B: z.number(),
  }),
  "post-flop": z.object({
    "C-bet": z.number(),
    FCB: z.number(),
    AF: z.number(),
    AFq: z.number(),
    WSD: z.number(),
    WTSD: z.number(),
    flop_donk_bet: z.number(),
  }),
  luck_level: z.string(),
  rank: z.object({
    score: z.number(),
    decisions: z.object({
      best: z.number(),
      good: z.number(),
      innacurate: z.number(),
      wrong: z.number(),
      blunder: z.number(),
    }),
  }),
});

export type PlayerStats = z.infer<typeof PlayerStatsSchema>;

export const PlayerCreateSchema = PlayerSchema.omit({ id: true }).partial({
  experience: true,
  enabled: true,
  status: true,
});

export type PlayerCreate = z.infer<typeof PlayerCreateSchema>;

export const PlayerUpdateSchema = PlayerSchema.omit({ id: true }).partial();

export type PlayerUpdate = z.infer<typeof PlayerUpdateSchema>;

export const SessionSchema = z.object({
  id: z.number(),
  player_id: z.string(),
  code: z.string().nullable(),
  loginCode: z.string().nullable(),
  token: z.string(),
  createdAt: z.string().datetime({ offset: true }),
  expireAt: z.string().datetime({ offset: true }),
});

export type Session = z.infer<typeof SessionSchema>;

export const BotSchema = z.object({
  id: z.number(),
  player_id: z.string(),
  table_id: z.string(),
  bot_id: z.string(),
  bot_configuration_id: z.string(),
  seat_id: z.string(),
});

export type Bot = z.infer<typeof BotSchema>;

export const BotCreateSchema = BotSchema.omit({ id: true });

export type BotCreate = z.infer<typeof BotCreateSchema>;

export const DrillAtomSuggestedActionSchema = z.object({
  action: z.enum(["bet", "call", "check", "fold", "raise", "allin"]),
  amount: z.number().optional().nullable(),
});

export type DrillAtomSuggestedAction = z.infer<
  typeof DrillAtomSuggestedActionSchema
>;

export const DrillAtomActionSchema = z.object({
  kind: z.literal("action"),
  actions: z.array(DrillAtomSuggestedActionSchema),
  selected: z.number(),
  position: z.string(),
  pot: z.number(),
});

export type DrillAtomAction = z.infer<typeof DrillAtomActionSchema>;

export const DrillAtomRoundSchema = z.object({
  kind: z.literal("round"),
  round: z.enum(["flop", "turn", "river"]),
  cards: z.string(),
});

export type DrillAtomRound = z.infer<typeof DrillAtomRoundSchema>;

export const DrillAtomSchema = z.discriminatedUnion("kind", [
  DrillAtomActionSchema,
  DrillAtomRoundSchema,
]);

export type DrillAtom = z.infer<typeof DrillAtomSchema>;

export const DrillSequenceSchema = z.object({
  id: z.number(),
  drillId: z.number(),
  sequence: z.array(DrillAtomSchema),
});

export type DrillSequence = z.infer<typeof DrillSequenceSchema>;

export const DrillSequenceCreateSchema = DrillSequenceSchema.omit({
  id: true,
});

export type DrillSequenceCreate = z.infer<typeof DrillSequenceCreateSchema>;

export const DrillSequenceUpdateSchema = DrillSequenceSchema.omit({
  id: true,
  drillId: true,
});

export type DrillSequenceUpdate = z.infer<typeof DrillSequenceUpdateSchema>;

export enum GameCodes {
  NLHE = "NLHE",
  OFC = "OFC",
}

export type GameCode = keyof typeof GameCodes;

export const PlayerMoveQualitySchema = z.object({
  id: z.number(),
  quality: z.enum([
    SuggestedActionQuality.Best,
    SuggestedActionQuality.Blunder,
    SuggestedActionQuality.Correct,
    SuggestedActionQuality.Inaccuracy,
    SuggestedActionQuality.Wrong,
  ]),
  sessionId: z.string().nullable(),
  playerId: z.string(),
  count: z.number(),
});

export type PlayerMoveQuality = z.infer<typeof PlayerMoveQualitySchema>;

export const PlayerMoveQualityCreateSchema = PlayerMoveQualitySchema.omit({
  id: true,
});

export type PlayerMoveQualityCreate = z.infer<
  typeof PlayerMoveQualityCreateSchema
>;

export const PlayerMoveQualityUpdateSchema = PlayerMoveQualitySchema.omit({
  id: true,
  playerId: true,
  sessionId: true,
}).partial();

export type PlayerMoveQualityUpdate = z.infer<
  typeof PlayerMoveQualityUpdateSchema
>;

export const PlayerStatisticSchema = z.object({
  id: z.number(),
  playerId: z.string(),
  avgEVLoss: z.number(),
  handsTotal: z.number(),
  totalEVLoss: z.number(),
  sessionId: z.string().nullable(),
  sessions: z.number(),
  score: z.number(),
  drillId: z.number().nullable().optional(),
});

export type PlayerStatistic = z.infer<typeof PlayerStatisticSchema>;

export const PlayerStatisticCreateSchema = PlayerStatisticSchema.omit({
  id: true,
});

export type PlayerStatisticCreate = z.infer<typeof PlayerStatisticCreateSchema>;

export const PlayerStatisticUpdateSchema = PlayerStatisticSchema.omit({
  id: true,
  playerId: true,
}).partial();

export type PlayerStatisticUpdate = z.infer<typeof PlayerStatisticUpdateSchema>;

export const PlayerHandHistorySchema = z.object({
  id: z.number(),
  sessionId: z.string().nullable().optional(),
  correctMoves: z.number(),
  totalMoves: z.number(),
  moves: z.array(PlayerLogSchema),
  playerId: z.string(),
  drillId: z.number().nullable().optional(),
  lastMoveAt: z.string().datetime({ offset: true }),
  createdAt: z.string().datetime({ offset: true }),
});

export type PlayerHandHistory = z.infer<typeof PlayerHandHistorySchema>;

export const PlayerHandHistoryCreateSchema = PlayerHandHistorySchema.omit({
  id: true,
}).partial({ createdAt: true });

export type PlayerHandHistoryCreate = z.infer<
  typeof PlayerHandHistoryCreateSchema
>;

export const PlayerHandHistoryUpdateSchema = PlayerHandHistorySchema.omit({
  id: true,
  playerId: true,
}).partial();

export type PlayerHandHistoryUpdate = z.infer<
  typeof PlayerHandHistoryUpdateSchema
>;

export type OrderBy = { field: string; direction: "asc" | "desc" };

export type Pagination = {
  limit?: number;
  offset?: number;
  orderBy?: OrderBy[];
};

export const suggestedActionQualityToCorrect = (
  quality?: SuggestedActionQuality | null,
) => {
  if (quality === SuggestedActionQuality.Best) {
    return true;
  }
  if (quality === SuggestedActionQuality.Correct) {
    return true;
  }
  // A5 team should fix api to return SuggestedActionQuality
  if ((quality as any) === "good") {
    return true;
  }
  return false;
};

export const DrillWithStatisticSchema = z.object({
  Drill: DrillSchema,
  PlayerStatistic: PlayerStatisticSchema.nullable(),
  PlayerHandHistory: PlayerHandHistorySchema.nullable(),
  DrillSequence: z.array(DrillSequenceSchema).nullable(),
});

export type DrillWithStatistic = z.infer<typeof DrillWithStatisticSchema>;

export type GetNextActionResult = (
  | Omit<DrillAtomAction, "selected">
  | Omit<DrillAtomRound, "cards">
  | { kind: "end" }
) & {
  players: ({
    stack: number;
    seat_no: number;
    position: PlayerPosition;
  } | null)[];
  button: number;
  pot: number;
};

export const TableSchema = z.object({
  id: z.number(),
  name: z.string().optional(),
  table_id: z.string().default(() => ulid()),
  gaming_configuration: GamingConfigurationSchema,

  max_players: z.number().min(2).max(9).default(9),

  currency: z.string().optional().default("USD"),

  learning_mode: z.boolean().default(false),
  assessment_mode: z.boolean().default(false),
  preconfigured_mode: z.boolean().default(false),

  starting_stack: z.number().gt(0).default(10000),
  rebuy_configuration: RebuyConfigurationSchema,

  bots_count: z.number().default(0),

  host_id: z.string().optional().nullable(),

  createdAt: z.string().datetime({ offset: true }),
  lastUpdatedAt: z.string().datetime({ offset: true }),
  lastPlayerActionAt: z.string().datetime({ offset: true }),
});

export type Table = z.infer<typeof TableSchema>;

export const TableCreateSchema = TableSchema.omit({
  id: true,
  lastUpdatedAt: true,
  lastPlayerActionAt: true,
  createdAt: true,
});

export type TableCreate = z.infer<typeof TableCreateSchema>;

export const ApiTableCreateSchema = z.object({
  tableConfig: TableSchema.pick({
    gaming_configuration: true,
    max_players: true,
    name: true,
    assessment_mode: true,
    preconfigured_mode: true,
  }),
  addBotsNumber: z.number().optional().nullable(),
  sitDownPlayer: z.boolean().optional().nullable(),
});
export type ApiTableCreate = z.infer<typeof ApiTableCreateSchema>;

export const TableUpdateSchema = TableSchema.omit({
  id: true,
  table_id: true,
}).partial();

export type TableUpdate = z.infer<typeof TableUpdateSchema>;

export enum BotType {
  GTO = "gto",
}

export const TablePlayerSeatSchema = z.object({
  tableId: z.string(),
  playerId: z.string(),
  seat: z.number(),
  meta: z
    .object({
      type: z.enum([BotType.GTO]),
    })
    .nullable(),
});

export type TablePlayerSeat = z.infer<typeof TablePlayerSeatSchema>;

export const TablePlayerSeatCreateSchema = TablePlayerSeatSchema;

export type TablePlayerSeatCreate = z.infer<typeof TablePlayerSeatCreateSchema>;

export const TableLobbySchema = z.object({
  table: TableSchema,
  playerCount: z.number(),
  botCount: z.number(),
});

export type TableLobby = z.infer<typeof TableLobbySchema>;

export type RoomTableConfig = PokerConfig & {
  numSeats: number;
  learningMode: boolean;
  assessmentMode: boolean;
  preConfiguredMode?: boolean;
  hostId?: string | null;
  startingStack: number;
  gameModeCode: string;
  flavors: GameFlavor[];
  stopAt: StopAt;
  rebuy: Table["rebuy_configuration"];
  handsToPlay?: number | null;
  skipTo?: Street;
};

export type FoldAction = { action: "fold" };
export type CheckAction = { action: "check" };
export type CallAction = { action: "call" };
export type BetAction = { action: "bet"; amount: number };
export type RaiseAction = { action: "raise"; amount: number };

export type PlayerActionWithoutAmount = FoldAction | CheckAction | CallAction;
export type PlayerActionWithAmount = BetAction | RaiseAction;
export type PlayerAction = PlayerActionWithoutAmount | PlayerActionWithAmount;

export type PlayerActionMeta = {
  createdAt: string;
  playerId: string;
  name: string;
};

export type PlayerActionWithMeta = PlayerAction & PlayerActionMeta;

export enum Env {
  Development = "development",
  Test = "test",
  Production = "production",
}

export const HandResultHandEndStateSchema = z.object({
  totalPotSize: z.number().int(),
  pots: z
    .array(
      z.object({
        eligiblePlayers: z.array(z.number()),
        size: z.number().int(),
      }),
    )
    .default([]),
});

export type HandResultHandEndState = z.infer<
  typeof HandResultHandEndStateSchema
>;

export const HandResultSchema = z.object({
  id: z.number().int(),
  tableId: z.string(),
  handId: z.string(),
  handUuid: z.string(),
  generalWinBet: z.number().int(),
  ledger: z.array(
    z.object({
      name: z.string().optional().nullable(),
      playerId: z.string(),
      seatNo: z.number().int(),
      stack: z.number().int(),
      bot: z.boolean(),
      net: z.number().int(),
      handNet: z.number().int(),
    }),
  ),
  communityCards: z.string().default(""),
  handHistory: z.array(ActionSchema).default([]),
  createdAt: z.string().datetime({ offset: true }),
  handEndState: HandResultHandEndStateSchema,
});

export type HandResult = z.infer<typeof HandResultSchema>;

export const HandResultCreateSchema = HandResultSchema.omit({
  id: true,
  createdAt: true,
});

export type HandResultCreate = z.infer<typeof HandResultCreateSchema>;

export const DrillFiltersSchema = z.object({
  heroPositionGroup: z.array(z.enum(PositionGroupFilterValues)).optional(),
  street: z.array(z.enum(StreetFilterValues)).optional(),
});

export type DrillFilters = z.infer<typeof DrillFiltersSchema>;

export const ApiAssessmentCreateSchema = z.object({
  maxHands: z.number().optional().nullable(),
});
export type ApiAssessmentCreate = z.infer<typeof ApiAssessmentCreateSchema>;

// Event schemas and types
export * from "./events/gameEvents";
export * from "./events/eventTypes";
export * from "./events/validators";
export * from "./action";
